import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { REPORT } from '@/constants/testIds';
import { trackPageView } from '@/lib/attribution';
import { Award, Radar, Shield, Terminal } from 'lucide-react';

const TIER_COLOR = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700' };

const Stat = ({ label, value, testId, accent }) => (
  <div className="border border-[#3F3F46] bg-[#18181B] p-5">
    <div className="c-label">{label}</div>
    <div
      data-testid={testId}
      className={`text-3xl md:text-4xl font-bold ${accent ? 'text-[#38BDF8]' : 'text-white'}`}
    >
      {value}
    </div>
  </div>
);

export const ReportCardPublicPage = () => {
  const { shortcode } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('report_card', shortcode);
    (async () => {
      try {
        const { data } = await api.get(`/report/${shortcode}`);
        setData(data);
      } catch (err) {
        setError(err?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [shortcode]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto mt-24 px-6 text-xs tracking-widest uppercase text-gray-500">
        FETCHING_REPORT<span className="caelix-dots"></span>
      </div>
    );
  }

  if (error === 'not_found' || !data) {
    return (
      <div className="max-w-2xl mx-auto mt-16 px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-yellow-400 mb-3">// REPORT_NOT_FOUND</div>
        <h1 className="text-3xl font-bold uppercase tracking-tighter mb-6">
          404 · Deactivated<span className="caelix-caret"></span>
        </h1>
        <div data-testid={REPORT.publicNotFound} className="c-card">
          <p className="text-xs text-gray-500 mb-4 leading-relaxed">
            &gt; This report card is either invalid or has been revoked by its owner.
          </p>
          <Link to="/leaderboard" className="c-btn-primary text-xs">SEE_LEADERBOARD</Link>
        </div>
      </div>
    );
  }

  const tierColor = data.milestone_tier ? TIER_COLOR[data.milestone_tier] : undefined;

  return (
    <div
      data-testid={REPORT.publicContainer}
      className="max-w-3xl mx-auto px-6 md:px-8 pt-10 pb-24"
    >
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // OPERATOR_REPORT_CARD · REDACTED
      </div>
      <h1 className="text-4xl sm:text-5xl font-bold uppercase tracking-tighter mb-6 flex items-center gap-4 flex-wrap">
        <Terminal className="w-10 h-10 text-[#38BDF8]" strokeWidth={2.5} />
        <span data-testid={REPORT.publicCallsign} className="text-[#38BDF8] tracking-tight">
          {data.callsign}
        </span>
        <span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 mb-8 leading-relaxed max-w-2xl">
        &gt; Public operator profile. No real names, emails, or entity values are exposed.
        &nbsp;Everything below is aggregate proof-of-work.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="c-card">
          <div className="c-label">TIER</div>
          <div
            data-testid={REPORT.publicTier}
            className="text-3xl font-bold uppercase tracking-tight"
            style={{ color: tierColor || '#a1a1aa' }}
          >
            {data.milestone_tier ? (
              <span className="inline-flex items-center gap-2">
                <Award className="w-6 h-6" strokeWidth={2.5} style={{ color: tierColor }} />
                {data.milestone_tier.toUpperCase()}
              </span>
            ) : (
              <span className="text-gray-500">—</span>
            )}
          </div>
        </div>
        <Stat
          label="PAID_REFERRALS"
          value={String(data.paid_referrals).padStart(2, '0')}
          testId={REPORT.publicPaidRefs}
          accent
        />
        <div className="c-card">
          <div className="c-label">JOINED</div>
          <div className="text-3xl font-bold text-white">{data.joined_month}</div>
        </div>
        <Stat
          label="WATCHED_ENTITIES"
          value={String(data.watched_entities).padStart(2, '0')}
          testId={REPORT.publicWatched}
        />
        <Stat
          label="ALERTS_PROCESSED"
          value={String(data.alerts_processed).padStart(3, '0')}
          testId={REPORT.publicAlerts}
        />
        <div className="c-card">
          <div className="c-label">PROTECTING_FOR</div>
          <div data-testid={REPORT.publicDays} className="text-3xl font-bold text-white">
            {data.days_protecting}
            <span className="text-sm text-gray-500 tracking-widest uppercase ml-1">d</span>
          </div>
        </div>
      </div>

      <div className="c-card mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 border-b border-[#3F3F46] pb-3 mb-4">
          // CERTIFICATION_LINE
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div className="flex items-center gap-2 text-gray-400">
            <Shield className="w-4 h-4 text-[#38BDF8]" strokeWidth={2.5} />
            <span>PII_REDACTED</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <Radar className="w-4 h-4 text-[#38BDF8]" strokeWidth={2.5} />
            <span>OPERATOR_VERIFIED</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 font-mono text-[10px]">
            SHARE :: {data.share_count}
          </div>
        </div>
      </div>

      <div className="c-card">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
          // WANT_YOUR_OWN?
        </div>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
          Caelix Intelligence is a terminal-native OSINT platform. Monitor domains, IPs, and
          emails · save keyword queries · earn free months by referring peers.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            data-testid={REPORT.publicCta}
            to="/signup"
            className="c-btn-primary text-xs"
          >
            CREATE_OPERATOR
          </Link>
          <Link to="/leaderboard" className="c-btn-secondary text-xs">SEE_LEADERBOARD</Link>
          <Link to="/pricing" className="c-btn-secondary text-xs">VIEW_PRICING</Link>
        </div>
      </div>

      <div className="mt-6 text-[10px] text-gray-600 tracking-widest uppercase text-center">
        &gt; generated {data.generated_at.slice(0, 19).replace('T', ' ')} · shortcode {data.shortcode}
      </div>
    </div>
  );
};

export default ReportCardPublicPage;
